import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, trendAPI, importAPI } from '../lib/api';
import { COLORS, formatCurrency, formatPercent, sumAccounts, getAccountById } from '../lib/utils';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { Loader2, Download, LogOut, FileText, Calendar, Printer } from 'lucide-react';
import PrintModal from '../components/PrintModal';

const SMJ_LOGO_URL = "https://customer-assets.emergentagent.com/job_funds-portal/artifacts/xi0cc8ut_Clean%20Logo%20with%20Words.png";

// Default data
const DEFAULT_ACCOUNTS = [
  {"id": "1000", "name": "Operating Cash", "type": "Asset", "balance": 214580},
  {"id": "1010", "name": "Payroll Cash", "type": "Asset", "balance": 45220},
  {"id": "1020", "name": "Savings Account", "type": "Asset", "balance": 125000},
  {"id": "1100", "name": "Accounts Receivable", "type": "Asset", "balance": 187450},
  {"id": "1200", "name": "Inventory", "type": "Asset", "balance": 94300},
  {"id": "1500", "name": "Equipment", "type": "Asset", "balance": 285000},
  {"id": "1510", "name": "Accum. Depr. - Equipment", "type": "Asset", "balance": -68400},
  {"id": "1600", "name": "Building", "type": "Asset", "balance": 520000},
  {"id": "1610", "name": "Accum. Depr. - Building", "type": "Asset", "balance": -78000},
  {"id": "2000", "name": "Accounts Payable", "type": "Liability", "balance": 63200},
  {"id": "2100", "name": "Accrued Payroll", "type": "Liability", "balance": 28500},
  {"id": "2110", "name": "Accrued Liabilities", "type": "Liability", "balance": 15800},
  {"id": "2200", "name": "Line of Credit", "type": "Liability", "balance": 75000},
  {"id": "2300", "name": "Note Payable - Equipment", "type": "Liability", "balance": 142000},
  {"id": "2310", "name": "Note Payable - Building", "type": "Liability", "balance": 385000},
  {"id": "3000", "name": "Common Stock", "type": "Equity", "balance": 100000},
  {"id": "3100", "name": "Retained Earnings", "type": "Equity", "balance": 435650},
  {"id": "4000", "name": "Product Sales", "type": "Income", "balance": 1245000},
  {"id": "4010", "name": "Service Revenue - Consulting", "type": "Income", "balance": 485000},
  {"id": "4020", "name": "Service Revenue - Maintenance", "type": "Income", "balance": 162000},
  {"id": "4050", "name": "Other Income", "type": "Income", "balance": 18500},
  {"id": "4900", "name": "Sales Returns & Allowances", "type": "Income", "balance": -32400},
  {"id": "5000", "name": "Cost of Goods Sold - Materials", "type": "COGS", "balance": 498000},
  {"id": "5010", "name": "Cost of Goods Sold - Labor", "type": "COGS", "balance": 312000},
  {"id": "5020", "name": "Cost of Goods Sold - Other", "type": "COGS", "balance": 87600},
  {"id": "6000", "name": "Salaries & Wages", "type": "Expense", "balance": 285000},
  {"id": "6010", "name": "Payroll Taxes", "type": "Expense", "balance": 21800},
  {"id": "6020", "name": "Employee Benefits", "type": "Expense", "balance": 42500},
  {"id": "6030", "name": "Retirement Contributions", "type": "Expense", "balance": 14250},
  {"id": "6100", "name": "Rent Expense", "type": "Expense", "balance": 0},
  {"id": "6110", "name": "Utilities", "type": "Expense", "balance": 18400},
  {"id": "6120", "name": "Property Taxes", "type": "Expense", "balance": 12600},
  {"id": "6200", "name": "Advertising", "type": "Expense", "balance": 28500},
  {"id": "6210", "name": "Marketing", "type": "Expense", "balance": 15200},
  {"id": "6300", "name": "Insurance", "type": "Expense", "balance": 24800},
  {"id": "6310", "name": "Professional Fees", "type": "Expense", "balance": 18500},
  {"id": "6400", "name": "Depreciation Expense", "type": "Expense", "balance": 38200},
  {"id": "6500", "name": "Office Supplies", "type": "Expense", "balance": 8400},
  {"id": "6510", "name": "Software & Subscriptions", "type": "Expense", "balance": 12800},
  {"id": "6520", "name": "Miscellaneous", "type": "Expense", "balance": 4200},
  {"id": "6900", "name": "Interest Expense", "type": "Expense", "balance": 28400},
];

const DEFAULT_TREND = [
  {"month": "Jan", "revenue": 142000, "netIncome": 18200},
  {"month": "Feb", "revenue": 138000, "netIncome": 16800},
  {"month": "Mar", "revenue": 165000, "netIncome": 24500},
  {"month": "Apr", "revenue": 158000, "netIncome": 21200},
  {"month": "May", "revenue": 172000, "netIncome": 28400},
  {"month": "Jun", "revenue": 168000, "netIncome": 26100},
  {"month": "Jul", "revenue": 145000, "netIncome": 19800},
  {"month": "Aug", "revenue": 152000, "netIncome": 22500},
  {"month": "Sep", "revenue": 178000, "netIncome": 31200},
  {"month": "Oct", "revenue": 185000, "netIncome": 34800},
  {"month": "Nov", "revenue": 192000, "netIncome": 38500},
  {"month": "Dec", "revenue": 283100, "netIncome": 58750},
];

// Card component
const Card = ({ children, style = {}, ...props }) => (
  <div 
    style={{
      background: COLORS.card,
      borderRadius: 12,
      border: `1px solid ${COLORS.border}`,
      padding: 24,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

export default function ClientPortal() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [accounts, setAccounts] = useState(DEFAULT_ACCOUNTS);
  const [trendData, setTrendData] = useState(DEFAULT_TREND);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState('pl');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showKpiSelector, setShowKpiSelector] = useState(false);
  const [selectedKpis, setSelectedKpis] = useState(['revenue', 'grossMargin', 'netMargin', 'currentRatio']);
  const today = new Date();
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const periodLabel = `${MONTHS[lastMonthEnd.getMonth()]} ${lastMonthEnd.getDate()}, ${lastMonthEnd.getFullYear()}`;
  const period = `Through ${periodLabel}`;
  const reportingYear = lastMonthEnd.getFullYear();
  const priorYear = reportingYear - 1;
  const balanceSheetDate = `As of ${periodLabel}`;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clientId = user?.client_id || 'demo';
      const [dataRes, trendRes] = await Promise.all([
        importAPI.getData(clientId),
        trendAPI.getData(clientId),
      ]);
      if (dataRes.data?.accounts) {
        setAccounts(dataRes.data.accounts);
      }
      if (trendRes.data) {
        setTrendData(trendRes.data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Financial calculations
  const sum = (ids) => sumAccounts(accounts, ids);
  const getAcc = (id) => getAccountById(accounts, id);

  const totalRevenue = sum(['4000', '4010', '4020', '4050', '4900']);
  const totalCOGS = sum(['5000', '5010', '5020']);
  const grossProfit = totalRevenue - totalCOGS;
  const compensation = sum(['6000', '6010', '6020', '6030']);
  const occupancy = sum(['6100', '6110', '6120']);
  const marketing = sum(['6200', '6210']);
  const insurance = getAcc('6300')?.balance || 0;
  const professional = getAcc('6310')?.balance || 0;
  const depreciation = getAcc('6400')?.balance || 0;
  const gaOther = sum(['6500', '6510', '6520']);
  const totalOpEx = compensation + occupancy + marketing + insurance + professional + depreciation + gaOther;
  const ebit = grossProfit - totalOpEx;
  const interest = getAcc('6900')?.balance || 0;
  const ebt = ebit - interest;
  const taxes = Math.round(ebt * 0.26);
  const netIncome = ebt - taxes;

  // Balance Sheet
  const cash = sum(['1000', '1010', '1020']);
  const ar = getAcc('1100')?.balance || 0;
  const inventory = getAcc('1200')?.balance || 0;
  const totalCA = cash + ar + inventory;
  const equipNet = sum(['1500', '1510']);
  const bldgNet = sum(['1600', '1610']);
  const totalFA = equipNet + bldgNet;
  const totalAssets = totalCA + totalFA;
  const ap = getAcc('2000')?.balance || 0;
  const accruedPay = getAcc('2100')?.balance || 0;
  const accruedLib = getAcc('2110')?.balance || 0;
  const loc = getAcc('2200')?.balance || 0;
  const totalCL = ap + accruedPay + accruedLib + loc;
  const noteEquip = getAcc('2300')?.balance || 0;
  const noteBldg = getAcc('2310')?.balance || 0;
  const totalLTD = noteEquip + noteBldg;
  const totalLiab = totalCL + totalLTD;
  const stock = getAcc('3000')?.balance || 0;
  const retEarnings = (getAcc('3100')?.balance || 0) + netIncome;
  const totalEquity = stock + retEarnings;
  const totalLE = totalLiab + totalEquity;

  // Get equity labels based on entity type (clients are typically LLCs)
  const getEquityLabels = (entityType) => {
    switch(entityType) {
      case 's_corp':
      case 'c_corp':
        return { equity: "STOCKHOLDERS' EQUITY", stock: 'Common Stock', retained: 'Retained Earnings' };
      case 'sole_prop':
        return { equity: "OWNER'S EQUITY", stock: "Owner's Capital", retained: "Owner's Draws" };
      case 'smllc':
      case 'multi_llc':
      case 'llc':
      default:
        return { equity: "MEMBERS' EQUITY", stock: "Members' Capital", retained: "Members' Draws & Distributions" };
    }
  };
  // Client portal uses user's company info, we'll default to LLC for now
  const equityLabels = getEquityLabels('llc');

  // Industry benchmarks for agriculture/farming (service business)
  const industryBenchmarks = {
    grossMargin: 45, // Typical gross margin for service businesses
    netMargin: 12,
    currentRatio: 2.0,
    debtToEquity: 1.5,
    returnOnAssets: 8,
    returnOnEquity: 15,
    daysReceivables: 45,
    inventoryTurnover: 6,
  };

  // Calculate additional KPIs
  const currentRatio = totalCA / totalCL;
  const debtToEquity = totalLiab / totalEquity;
  const returnOnAssets = (netIncome / totalAssets) * 100;
  const returnOnEquity = (netIncome / totalEquity) * 100;
  const daysReceivables = (ar / totalRevenue) * 365;
  const inventoryTurnover = totalCOGS / inventory;
  const workingCapital = totalCA - totalCL;
  const operatingMargin = (ebit / totalRevenue) * 100;
  const ebitda = ebit + depreciation;
  const quickRatio = (cash + ar) / totalCL;

  // All available KPIs with benchmarks
  const allKpis = {
    revenue: { 
      label: 'Total Revenue', 
      value: formatCurrency(totalRevenue, true), 
      rawValue: totalRevenue,
      sub: period, 
      color: COLORS.primary,
      benchmark: null,
    },
    grossMargin: { 
      label: 'Gross Margin', 
      value: formatPercent(grossProfit, totalRevenue), 
      rawValue: (grossProfit / totalRevenue) * 100,
      sub: `Industry avg: ${industryBenchmarks.grossMargin}%`, 
      color: COLORS.success,
      benchmark: industryBenchmarks.grossMargin,
    },
    netMargin: { 
      label: 'Net Margin', 
      value: formatPercent(netIncome, totalRevenue), 
      rawValue: (netIncome / totalRevenue) * 100,
      sub: `Industry avg: ${industryBenchmarks.netMargin}%`, 
      color: netIncome > 0 ? COLORS.success : COLORS.error,
      benchmark: industryBenchmarks.netMargin,
    },
    currentRatio: { 
      label: 'Current Ratio', 
      value: currentRatio.toFixed(2) + 'x', 
      rawValue: currentRatio,
      sub: `Industry avg: ${industryBenchmarks.currentRatio}x`, 
      color: currentRatio >= 1.5 ? COLORS.success : COLORS.error,
      benchmark: industryBenchmarks.currentRatio,
    },
    netIncome: { 
      label: 'Net Income', 
      value: formatCurrency(netIncome, true), 
      rawValue: netIncome,
      sub: 'After taxes', 
      color: netIncome > 0 ? COLORS.success : COLORS.error,
      benchmark: null,
    },
    totalAssets: { 
      label: 'Total Assets', 
      value: formatCurrency(totalAssets, true), 
      rawValue: totalAssets,
      sub: 'Balance Sheet', 
      color: COLORS.primary,
      benchmark: null,
    },
    cashBalance: { 
      label: 'Cash Balance', 
      value: formatCurrency(cash, true), 
      rawValue: cash,
      sub: 'Liquid assets', 
      color: COLORS.primary,
      benchmark: null,
    },
    workingCapital: { 
      label: 'Working Capital', 
      value: formatCurrency(workingCapital, true), 
      rawValue: workingCapital,
      sub: 'CA - CL', 
      color: workingCapital > 0 ? COLORS.success : COLORS.error,
      benchmark: null,
    },
    debtToEquity: { 
      label: 'Debt to Equity', 
      value: debtToEquity.toFixed(2) + 'x', 
      rawValue: debtToEquity,
      sub: `Industry avg: ${industryBenchmarks.debtToEquity}x`, 
      color: debtToEquity <= 2 ? COLORS.success : COLORS.error,
      benchmark: industryBenchmarks.debtToEquity,
    },
    returnOnAssets: { 
      label: 'Return on Assets', 
      value: returnOnAssets.toFixed(1) + '%', 
      rawValue: returnOnAssets,
      sub: `Industry avg: ${industryBenchmarks.returnOnAssets}%`, 
      color: returnOnAssets >= 5 ? COLORS.success : COLORS.muted,
      benchmark: industryBenchmarks.returnOnAssets,
    },
    returnOnEquity: { 
      label: 'Return on Equity', 
      value: returnOnEquity.toFixed(1) + '%', 
      rawValue: returnOnEquity,
      sub: `Industry avg: ${industryBenchmarks.returnOnEquity}%`, 
      color: returnOnEquity >= 10 ? COLORS.success : COLORS.muted,
      benchmark: industryBenchmarks.returnOnEquity,
    },
    daysReceivables: { 
      label: 'Days Receivables', 
      value: Math.round(daysReceivables) + ' days', 
      rawValue: daysReceivables,
      sub: `Industry avg: ${industryBenchmarks.daysReceivables} days`, 
      color: daysReceivables <= 45 ? COLORS.success : COLORS.error,
      benchmark: industryBenchmarks.daysReceivables,
    },
    ebitda: { 
      label: 'EBITDA', 
      value: formatCurrency(ebitda, true), 
      rawValue: ebitda,
      sub: 'Earnings metric', 
      color: ebitda > 0 ? COLORS.success : COLORS.error,
      benchmark: null,
    },
    operatingMargin: { 
      label: 'Operating Margin', 
      value: operatingMargin.toFixed(1) + '%', 
      rawValue: operatingMargin,
      sub: 'EBIT / Revenue', 
      color: operatingMargin > 10 ? COLORS.success : COLORS.muted,
      benchmark: null,
    },
    quickRatio: { 
      label: 'Quick Ratio', 
      value: quickRatio.toFixed(2) + 'x', 
      rawValue: quickRatio,
      sub: '(Cash + AR) / CL', 
      color: quickRatio >= 1 ? COLORS.success : COLORS.error,
      benchmark: null,
    },
  };

  // Get selected KPIs for display
  const displayKpis = selectedKpis.map(key => allKpis[key]).filter(Boolean);

  const toggleKpi = (kpiKey) => {
    if (selectedKpis.includes(kpiKey)) {
      if (selectedKpis.length > 1) {
        setSelectedKpis(selectedKpis.filter(k => k !== kpiKey));
      }
    } else if (selectedKpis.length < 6) {
      setSelectedKpis([...selectedKpis, kpiKey]);
    }
  };

  // Old kpis array for backward compatibility
  const kpis = displayKpis;

  const PLRow = ({ label, value, bold, indent = 0, total, color }) => (
    <tr style={{ borderBottom: total ? `2px solid ${COLORS.dark}` : 'none' }}>
      <td style={{ padding: '7px 0', paddingLeft: indent * 24, fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: color || COLORS.dark }}>{label}</td>
      <td style={{ padding: '7px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: color || (value < 0 ? COLORS.error : COLORS.dark) }}>
        {value !== undefined ? formatCurrency(value) : ''}
      </td>
      <td style={{ padding: '7px 0 7px 20px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.muted }}>
        {value !== undefined ? formatPercent(value, totalRevenue) : ''}
      </td>
    </tr>
  );

  const BSRow = ({ label, value, bold, indent = 0, total }) => (
    <tr style={{ borderBottom: total ? `2px solid ${COLORS.dark}` : 'none' }}>
      <td style={{ padding: '6px 0', paddingLeft: indent * 20, fontSize: bold ? 13 : 12, fontWeight: bold ? 700 : 400, color: COLORS.dark }}>{label}</td>
      <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: bold ? 13 : 12, fontWeight: bold ? 700 : 400, color: value < 0 ? COLORS.error : COLORS.dark }}>
        {value !== undefined ? formatCurrency(value) : ''}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.bg }}>
        <Loader2 size={32} className="animate-spin" color={COLORS.primary} />
      </div>
    );
  }

  return (
    <div data-testid="client-portal" style={{ minHeight: '100vh', background: COLORS.bg }}>
      {/* Top Bar */}
      <div style={{ background: COLORS.dark, borderBottom: `3px solid ${COLORS.primary}`, padding: '0 40px', display: 'flex', alignItems: 'center', gap: 24, height: 80 }}>
        <img 
          src={SMJ_LOGO_URL} 
          alt="Steele Martin Jones" 
          style={{ height: 80, width: 'auto' }}
        />
        <div style={{ width: 1, height: 28, background: COLORS.subtle }} />
        <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{user?.name}</span>
        <span style={{ fontSize: 12, color: COLORS.muted }}>{user?.company}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{
            padding: '4px 10px',
            borderRadius: 6,
            background: COLORS.success,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            Client View
          </span>
          <span style={{
            padding: '4px 10px',
            borderRadius: 6,
            background: COLORS.subtle,
            color: COLORS.muted,
            fontSize: 11,
            fontWeight: 500,
          }}>
            Last sync: Today, 2:14 PM
          </span>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: `1px solid ${COLORS.subtle}`,
              background: 'transparent',
              color: COLORS.muted,
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '40px 40px 80px', maxWidth: 1000, margin: '0 auto' }} id="printable-report">
        {/* Print Modal - Client version (no CPA assurance, no firm info) */}
        <PrintModal 
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          accounts={accounts}
          client={{ company: user?.company, name: user?.name }}
          currentStatement={activeReport}
          trendData={trendData}
          userRole="client"
        />

        {/* Header */}
        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <h2 className="font-heading" style={{ fontSize: 28, fontWeight: 500, color: COLORS.dark, margin: '0 0 4px' }}>
              {user?.company || 'Your Company'}
            </h2>
            <p style={{ color: COLORS.muted, margin: 0, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={14} /> {period}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              data-testid="print-btn"
              onClick={() => setShowPrintModal(true)}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: `2px solid ${COLORS.border}`,
                background: COLORS.card,
                color: COLORS.dark,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Printer size={16} /> Print
            </button>
            <button
              data-testid="export-pdf-btn"
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: `2px solid ${COLORS.border}`,
                background: COLORS.card,
                color: COLORS.dark,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Download size={16} /> Download PDF
            </button>
          </div>
        </div>

        {/* KPI Strip with Customize Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted }}>
            Your Financial KPIs
          </div>
          <button
            onClick={() => setShowKpiSelector(!showKpiSelector)}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              background: showKpiSelector ? COLORS.primaryLight : COLORS.card,
              color: showKpiSelector ? COLORS.primary : COLORS.muted,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showKpiSelector ? 'Done' : 'Customize KPIs'}
          </button>
        </div>

        {/* KPI Selector */}
        {showKpiSelector && (
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>
              Select up to 6 KPIs to display on your dashboard. KPIs with industry benchmarks show how you compare.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {Object.entries(allKpis).map(([key, kpi]) => (
                <button
                  key={key}
                  onClick={() => toggleKpi(key)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: `2px solid ${selectedKpis.includes(key) ? COLORS.primary : COLORS.border}`,
                    background: selectedKpis.includes(key) ? COLORS.primaryLight : '#fff',
                    cursor: selectedKpis.includes(key) || selectedKpis.length < 6 ? 'pointer' : 'not-allowed',
                    opacity: !selectedKpis.includes(key) && selectedKpis.length >= 6 ? 0.5 : 1,
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: selectedKpis.includes(key) ? COLORS.primary : COLORS.dark }}>{kpi.label}</div>
                  {kpi.benchmark && (
                    <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>vs. Industry</div>
                  )}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* KPI Grid */}
        <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(displayKpis.length, 4)}, 1fr)`, gap: 16, marginBottom: 28 }}>
          {displayKpis.map(k => (
            <Card key={k.label} className="kpi-card" style={{ padding: 20, borderTop: `4px solid ${k.color}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 8 }}>{k.label}</div>
              <div className="font-heading" style={{ fontSize: 26, fontWeight: 500, color: COLORS.dark, marginBottom: 4 }}>{k.value}</div>
              <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: k.benchmark ? 6 : 0 }}>{k.sub}</div>
              {k.benchmark && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ 
                    flex: 1, 
                    height: 4, 
                    background: COLORS.border, 
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}>
                    <div style={{ 
                      width: `${Math.min((k.rawValue / k.benchmark) * 50, 100)}%`, 
                      height: '100%', 
                      background: k.rawValue >= k.benchmark ? COLORS.success : COLORS.error,
                      borderRadius: 2,
                    }} />
                  </div>
                  <div style={{ 
                    fontSize: 10, 
                    fontWeight: 600, 
                    color: k.rawValue >= k.benchmark ? COLORS.success : COLORS.error 
                  }}>
                    {k.rawValue >= k.benchmark ? 'Above' : 'Below'} avg
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Trend Chart - Hidden on print */}
        <Card className="no-print trend-chart-container" style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 16 }}>
            Revenue & Net Income — {reportingYear}
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={trendData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.muted }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 10, fill: COLORS.muted }} axisLine={false} tickLine={false} width={52} />
              <Tooltip 
                formatter={(v, n) => [formatCurrency(v), n === 'revenue' ? 'Revenue' : 'Net Income']}
                contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 12 }}
              />
              <Line type="monotone" dataKey="revenue" stroke={COLORS.primary} strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="netIncome" stroke={COLORS.success} strokeWidth={2} dot={false} strokeDasharray="4 3" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Statement Tabs - Hidden on print */}
        <div className="no-print" style={{ display: 'flex', gap: 2, marginBottom: 24, background: COLORS.off, borderRadius: 10, padding: 4, width: 'fit-content' }}>
          {[['pl', 'Income Statement'], ['bs', 'Balance Sheet'], ['cf', 'Cash Flow']].map(([k, l]) => (
            <button
              key={k}
              data-testid={`report-tab-${k}`}
              onClick={() => setActiveReport(k)}
              style={{
                padding: '9px 22px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                background: activeReport === k ? COLORS.card : 'transparent',
                color: activeReport === k ? COLORS.dark : COLORS.muted,
                cursor: 'pointer',
                boxShadow: activeReport === k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Income Statement */}
        {activeReport === 'pl' && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Income Statement</h3>
                <div style={{ fontSize: 12, color: COLORS.muted }}>{period}</div>
              </div>
              <div style={{ display: 'flex', gap: 24 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>EBITDA</div>
                  <div style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, color: COLORS.dark }}>{formatCurrency(ebit + depreciation)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Net Margin</div>
                  <div style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, color: netIncome > 0 ? COLORS.success : COLORS.error }}>{formatPercent(netIncome, totalRevenue)}</div>
                </div>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                  <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Description</th>
                  <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{reportingYear}</th>
                  <th style={{ padding: '8px 0 8px 20px', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>% Rev</th>
                </tr>
              </thead>
              <tbody>
                <PLRow label="REVENUE" bold color={COLORS.muted} />
                <PLRow label="Service Revenue" value={sum(['4010', '4020'])} indent={1} />
                <PLRow label="Product Sales" value={sum(['4000', '4900'])} indent={1} />
                <PLRow label="Other Income" value={sum(['4050'])} indent={1} />
                <PLRow label="Total Revenue" value={totalRevenue} bold total />
                <PLRow label="" />
                <PLRow label="COST OF REVENUE" bold color={COLORS.muted} />
                <PLRow label="Direct Materials" value={sum(['5000'])} indent={1} />
                <PLRow label="Direct Labor" value={sum(['5010'])} indent={1} />
                <PLRow label="Other COGS" value={sum(['5020'])} indent={1} />
                <PLRow label="Total Cost of Revenue" value={totalCOGS} bold total />
                <PLRow label="GROSS PROFIT" value={grossProfit} bold total color={grossProfit > 0 ? COLORS.success : COLORS.error} />
                <PLRow label="" />
                <PLRow label="OPERATING EXPENSES" bold color={COLORS.muted} />
                <PLRow label="Compensation & Benefits" value={compensation} indent={1} />
                <PLRow label="Occupancy" value={occupancy} indent={1} />
                <PLRow label="Marketing & Advertising" value={marketing} indent={1} />
                <PLRow label="Insurance" value={insurance} indent={1} />
                <PLRow label="Professional Fees" value={professional} indent={1} />
                <PLRow label="Depreciation & Amortization" value={depreciation} indent={1} />
                <PLRow label="G&A — Other" value={gaOther} indent={1} />
                <PLRow label="Total Operating Expenses" value={totalOpEx} bold total />
                <PLRow label="INCOME FROM OPERATIONS (EBIT)" value={ebit} bold total color={ebit > 0 ? COLORS.dark : COLORS.error} />
                <PLRow label="" />
                <PLRow label="OTHER INCOME / (EXPENSE)" bold color={COLORS.muted} />
                <PLRow label="Interest Expense" value={-interest} indent={1} />
                <PLRow label="NET INCOME" value={ebit - interest} bold total color={(ebit - interest) > 0 ? COLORS.success : COLORS.error} />
              </tbody>
            </table>
          </Card>
        )}

        {/* Balance Sheet */}
        {activeReport === 'bs' && (
          <Card>
            <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Balance Sheet</h3>
            <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 24 }}>{balanceSheetDate}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Assets</th>
                    <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <BSRow label="CURRENT ASSETS" bold />
                  <BSRow label="Cash & Equivalents" value={cash} indent={1} />
                  <BSRow label="Accounts Receivable" value={ar} indent={1} />
                  <BSRow label="Inventory" value={inventory} indent={1} />
                  <BSRow label="Total Current Assets" value={totalCA} bold total />
                  <BSRow label="" />
                  <BSRow label="PROPERTY & EQUIPMENT" bold />
                  <BSRow label="Equipment, net" value={equipNet} indent={1} />
                  <BSRow label="Building, net" value={bldgNet} indent={1} />
                  <BSRow label="Total Fixed Assets" value={totalFA} bold total />
                  <BSRow label="" />
                  <BSRow label="TOTAL ASSETS" value={totalAssets} bold total />
                </tbody>
              </table>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Liabilities & Equity</th>
                    <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <BSRow label="CURRENT LIABILITIES" bold />
                  <BSRow label="Accounts Payable" value={ap} indent={1} />
                  <BSRow label="Accrued Payroll" value={accruedPay} indent={1} />
                  <BSRow label="Accrued Liabilities" value={accruedLib} indent={1} />
                  <BSRow label="Line of Credit" value={loc} indent={1} />
                  <BSRow label="Total Current Liabilities" value={totalCL} bold total />
                  <BSRow label="" />
                  <BSRow label="LONG-TERM DEBT" bold />
                  <BSRow label="Note Payable — Equipment" value={noteEquip} indent={1} />
                  <BSRow label="Note Payable — Building" value={noteBldg} indent={1} />
                  <BSRow label="Total Long-Term Debt" value={totalLTD} bold total />
                  <BSRow label="TOTAL LIABILITIES" value={totalLiab} bold />
                  <BSRow label="" />
                  <BSRow label={equityLabels.equity} bold />
                  <BSRow label={equityLabels.stock} value={stock} indent={1} />
                  <BSRow label={equityLabels.retained} value={retEarnings} indent={1} />
                  <BSRow label="Total Equity" value={totalEquity} bold total />
                  <BSRow label="" />
                  <BSRow label="TOTAL LIABILITIES & EQUITY" value={totalLE} bold total />
                </tbody>
              </table>
            </div>
            <div style={{
              marginTop: 20,
              padding: '12px 16px',
              background: Math.abs(totalAssets - totalLE) < 1 ? '#F0FDF4' : '#FEF2F2',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: Math.abs(totalAssets - totalLE) < 1 ? COLORS.success : COLORS.error,
            }}>
              {Math.abs(totalAssets - totalLE) < 1 ? '✓ Balance sheet is in balance' : `⚠ Out of balance by ${formatCurrency(Math.abs(totalAssets - totalLE))}`}
            </div>
          </Card>
        )}

        {/* Cash Flow */}
        {activeReport === 'cf' && (
          <Card>
            <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Statement of Cash Flows</h3>
            <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 24 }}>{period} · Indirect Method</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'OPERATING ACTIVITIES', bold: true },
                  { label: 'Net Income', value: netIncome, indent: 1 },
                  { label: 'Add: Depreciation & Amortization', value: depreciation, indent: 1 },
                  { label: 'Changes in Accounts Receivable', value: -31000, indent: 1 },
                  { label: 'Changes in Inventory', value: 14000, indent: 1 },
                  { label: 'Changes in Accounts Payable', value: 22000, indent: 1 },
                  { label: 'Changes in Accrued Liabilities', value: 8500, indent: 1 },
                  { label: 'Net Cash from Operating Activities', value: netIncome + depreciation - 31000 + 14000 + 22000 + 8500, bold: true, total: true },
                  { label: '' },
                  { label: 'INVESTING ACTIVITIES', bold: true },
                  { label: 'Purchase of Equipment', value: -85000, indent: 1 },
                  { label: 'Capital Improvements — Building', value: -42000, indent: 1 },
                  { label: 'Net Cash from Investing Activities', value: -127000, bold: true, total: true },
                  { label: '' },
                  { label: 'FINANCING ACTIVITIES', bold: true },
                  { label: 'Repayment of Notes Payable', value: -48000, indent: 1 },
                  { label: 'Draws on Line of Credit', value: 35000, indent: 1 },
                  { label: 'Distributions to Owners', value: -72000, indent: 1 },
                  { label: 'Net Cash from Financing Activities', value: -85000, bold: true, total: true },
                  { label: '' },
                  { label: 'NET CHANGE IN CASH', value: (netIncome + depreciation - 31000 + 14000 + 22000 + 8500) - 127000 - 85000, bold: true },
                  { label: 'Beginning Cash Balance', value: 341000, indent: 1 },
                  { label: 'ENDING CASH BALANCE', value: 341000 + ((netIncome + depreciation - 31000 + 14000 + 22000 + 8500) - 127000 - 85000), bold: true, total: true },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: row.total ? `2px solid ${COLORS.dark}` : 'none' }}>
                    <td style={{ padding: '6px 0', paddingLeft: (row.indent || 0) * 20, fontSize: row.bold ? 13 : 12, fontWeight: row.bold ? 700 : 400, color: COLORS.dark }}>{row.label}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: row.bold ? 13 : 12, fontWeight: row.bold ? 700 : 400, color: row.value < 0 ? COLORS.error : COLORS.dark }}>
                      {row.value !== undefined ? formatCurrency(row.value) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
